import { Icon } from 'leaflet';

/* Icon usage in map
  
Use default icon (same as before)
const defaultIcon = assetIcon;

Create a custom icon
shape: Choose between 'circle', 'square', or 'triangle' (default: 'circle')
fillColor: The fill color of the shape (default: '#0078FF')
strokeColor: The stroke (border) color (default: '#FFFFFF')
strokeWidth: The width of the stroke (default: 2)
size: The overall size of the icon (default: 30)
opacity: The transparency level (default: 1)


Example
const customSquareIcon = createAssetIcon({
  shape: 'square',
  fillColor: '#FF0000',
  strokeColor: '#000000',
  size: 40,
  opacity: 0.8
});

Create an icon with just some partial properties
const partialCustomIcon = createAssetIcon({
  fillColor: '#00FF00'
});
*/

interface AssetIconOptions {
  shape?: 'circle' | 'square' | 'triangle';
  fillColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
  size?: number;
  opacity?: number;
}

export const createAssetIcon = ({
  shape = 'circle',
  fillColor = '#0B2265',
  strokeColor = '#F3F6FA',
  strokeWidth = 2,
  size = 30,
  opacity = 1,
}: AssetIconOptions = {}) => {
  const iconSize = size / 2;
  const shapes = {
    circle: `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - strokeWidth / 2}" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" opacity="${opacity}"/>`,
    square: `<rect x="${strokeWidth / 2}" y="${strokeWidth / 2}" width="${size - strokeWidth}" height="${size - strokeWidth}" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" opacity="${opacity}"/>`,
    triangle: `<polygon points="${size / 2},${strokeWidth} ${strokeWidth},${size - strokeWidth} ${size - strokeWidth},${size - strokeWidth}" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" opacity="${opacity}"/>`,
  };

  return new Icon({
    iconUrl:
      'data:image/svg+xml;charset=utf-8,' +
      encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none">
            ${shapes[shape]}
          </svg>
        `),
    iconSize: [iconSize, iconSize],
    iconAnchor: [iconSize / 2, iconSize],
    popupAnchor: [0, -size],
  });
};

// Default asset icon for backward compatibility
export const assetIcon = createAssetIcon();

export const markerIcon = new Icon({
  iconUrl:
    'data:image/svg+xml;charset=utf-8,' +
    encodeURIComponent(`
   <svg width="34" height="52" viewBox="0 0 34 52" fill="none" xmlns="http://www.w3.org/2000/svg">
<mask id="path-1-outside-1_2189_1003" maskUnits="userSpaceOnUse" x="0" y="0" width="34" height="52" fill="black">
<rect fill="white" width="34" height="52"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M17 47C17 47 31 24.732 31 17C31 9.26801 24.732 3 17 3C9.26801 3 3 9.26801 3 17C3 24.732 17 47 17 47ZM17 25C21.4183 25 25 21.4183 25 17C25 12.5817 21.4183 9 17 9C12.5817 9 9 12.5817 9 17C9 21.4183 12.5817 25 17 25Z"/>
</mask>
<path fill-rule="evenodd" clip-rule="evenodd" d="M17 47C17 47 31 24.732 31 17C31 9.26801 24.732 3 17 3C9.26801 3 3 9.26801 3 17C3 24.732 17 47 17 47ZM17 25C21.4183 25 25 21.4183 25 17C25 12.5817 21.4183 9 17 9C12.5817 9 9 12.5817 9 17C9 21.4183 12.5817 25 17 25Z" fill="#B61F3D"/>
<path d="M17 47L14.8835 48.3306L17 51.697L19.1165 48.3306L17 47ZM28.5 17C28.5 18.3874 27.8151 20.8183 26.5021 23.9943C25.2347 27.0603 23.5278 30.4868 21.7963 33.719C20.069 36.9434 18.3386 39.9348 17.0384 42.1226C16.3889 43.2155 15.8481 44.1054 15.4706 44.7208C15.2819 45.0284 15.134 45.2672 15.0339 45.4284C14.9838 45.5089 14.9457 45.57 14.9204 45.6106C14.9077 45.6308 14.8982 45.646 14.892 45.6558C14.8889 45.6607 14.8867 45.6643 14.8853 45.6666C14.8846 45.6677 14.8841 45.6685 14.8838 45.669C14.8836 45.6692 14.8836 45.6693 14.8835 45.6694C14.8835 45.6694 14.8835 45.6694 17 47C19.1165 48.3306 19.1166 48.3304 19.1168 48.3301C19.117 48.3298 19.1172 48.3294 19.1175 48.329C19.1181 48.3281 19.1188 48.3268 19.1198 48.3253C19.1218 48.3221 19.1246 48.3176 19.1283 48.3118C19.1355 48.3002 19.1461 48.2833 19.16 48.2612C19.1876 48.217 19.2281 48.1521 19.2805 48.0677C19.3855 47.8989 19.5384 47.6517 19.7325 47.3354C20.1206 46.7028 20.6736 45.7926 21.3366 44.677C22.6614 42.4479 24.431 39.3891 26.2037 36.08C27.9722 32.7787 29.7653 29.1884 31.1229 25.9045C32.4349 22.7306 33.5 19.4786 33.5 17H28.5ZM17 5.5C23.3513 5.5 28.5 10.6487 28.5 17H33.5C33.5 7.8873 26.1127 0.5 17 0.5V5.5ZM5.5 17C5.5 10.6487 10.6487 5.5 17 5.5V0.5C7.8873 0.5 0.5 7.8873 0.5 17H5.5ZM17 47C19.1165 45.6694 19.1165 45.6694 19.1165 45.6694C19.1164 45.6693 19.1164 45.6692 19.1162 45.669C19.1159 45.6685 19.1154 45.6677 19.1147 45.6666C19.1133 45.6643 19.1111 45.6607 19.108 45.6558C19.1018 45.646 19.0923 45.6308 19.0796 45.6106C19.0543 45.57 19.0162 45.5089 18.9661 45.4284C18.866 45.2672 18.7181 45.0284 18.5294 44.7208C18.1519 44.1054 17.6111 43.2155 16.9616 42.1226C15.6614 39.9348 13.931 36.9434 12.2037 33.719C10.4722 30.4868 8.76533 27.0603 7.49787 23.9943C6.18493 20.8183 5.5 18.3874 5.5 17H0.5C0.5 19.4786 1.56507 22.7306 2.87713 25.9045C4.23467 29.1884 6.02778 32.7787 7.79629 36.08C9.56898 39.3891 11.3386 42.4479 12.6634 44.677C13.3264 45.7926 13.8794 46.7028 14.2675 47.3354C14.4616 47.6517 14.6145 47.8989 14.7195 48.0677C14.7719 48.1521 14.8124 48.217 14.84 48.2612C14.8539 48.2833 14.8645 48.3002 14.8717 48.3118C14.8754 48.3176 14.8782 48.3221 14.8802 48.3253C14.8812 48.3268 14.8819 48.3281 14.8825 48.329C14.8828 48.3294 14.883 48.3298 14.8832 48.3301C14.8834 48.3304 14.8835 48.3306 17 47ZM22.5 17C22.5 20.0376 20.0376 22.5 17 22.5V27.5C22.799 27.5 27.5 22.799 27.5 17H22.5ZM17 11.5C20.0376 11.5 22.5 13.9624 22.5 17H27.5C27.5 11.201 22.799 6.5 17 6.5V11.5ZM11.5 17C11.5 13.9624 13.9624 11.5 17 11.5V6.5C11.201 6.5 6.5 11.201 6.5 17H11.5ZM17 22.5C13.9624 22.5 11.5 20.0376 11.5 17H6.5C6.5 22.799 11.201 27.5 17 27.5V22.5Z" fill="white" mask="url(#path-1-outside-1_2189_1003)"/>
</svg>
    `),
  iconSize: [28, 48],
  iconAnchor: [14, 55],
  popupAnchor: [0, -30],
  zIndexOffset: 1,
});
